// A photo reorder or "Set as main" saved offline (BL-2418, ADR 066 — Offline Mode § 4.6): the queue
// entry that replays it through `PUT /api/lots/:id/media/order` (BL-2417), and the device's copy of
// the lot in the new order, both folded by the one order rule the server runs (`applyLotMediaOrder`).
import type { InventoryMediaItem } from '@auctogon/core/inventory';
import type { OfflineDeltaLotDto } from '@auctogon/core-admin/schemas/responses/offline-sync';

import { applyLotMediaOrder } from '../lots/lot-attachment-edit';
import { buildQueuedEntry, type ChangeQueueLabel, type QueuedEntryInput } from './change-queue-entry';

/**
 * The queue entry for a Save's photo order. Queue it after the same Save's photo adds and removes:
 * the replay holds it until every earlier photo edit of the lot has left the queue (BL-2418), so a
 * photo picked offline exists on the server before the order names it.
 *
 * @param input.id - a client-minted UUID.
 * @param input.lotId - the lot's server id, or a new lot's client id.
 * @param input.label - how the queue names the lot, captured now.
 * @param input.dependsOn - the lot's queued create, for a lot made offline; else null.
 * @param input.order - the photo ids in their new order; the first is Main.
 * @param input.baseOrder - the photo ids as the device held them before this Save, so the server
 *   refuses the order (409 `STALE_FIELDS`) when another device has reordered them since.
 * @returns the outbox append input.
 */
export function mediaOrderQueueEntry(input: {
  id: string;
  lotId: string;
  label: ChangeQueueLabel;
  dependsOn: string | null;
  order: readonly string[];
  baseOrder: readonly string[];
}): QueuedEntryInput {
  return buildQueuedEntry({
    id: input.id,
    op: 'lot.media.order',
    call: { params: { id: input.lotId }, query: null, body: { ids: [...input.order], baseOrder: [...input.baseOrder] } },
    label: input.label,
    lotId: input.lotId,
    dependsOn: input.dependsOn,
  });
}

/**
 * The list row's photos in the new order: its cover follows Main (operator Q3, 2026-10-12). The
 * Save's adds are already on the row (BUG-2081), so a photo picked offline and made Main is the
 * row's cover too.
 */
function rowMediaInOrder(row: readonly InventoryMediaItem[], order: readonly string[]): InventoryMediaItem[] {
  const ordered = applyLotMediaOrder(row, order);
  return 'next' in ordered ? ordered.next : [...row];
}

/**
 * The device's copy of a lot after a Save's photo order: the detail's photos in that order with the
 * first as Main, and the list row following it.
 *
 * @param input.lot - the cached lot, after the Save's photo adds and removes.
 * @param input.order - the photo ids in their new order.
 * @returns the lot; unchanged when the order is already in place.
 */
export function applyOfflineMediaOrder(input: { lot: OfflineDeltaLotDto; order: readonly string[] }): OfflineDeltaLotDto {
  const { lot, order } = input;
  const ordered = applyLotMediaOrder(lot.detail.media, order);
  if (!('next' in ordered)) return lot;
  const media = ordered.next;
  return {
    ...lot,
    detail: { ...lot.detail, media },
    row: lot.row.media ? { ...lot.row, media: rowMediaInOrder(lot.row.media, order) } : lot.row,
  };
}
